import { useState, type JSX } from 'react';
import { z } from 'zod';
import {
  memoryPlanSchema,
  MEMORY_ENGINE_VERSION,
  memorySettingsSchema,
  formatExactMoney,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath, request, savedSchema, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import { dateLabel, type Copy, type Language } from './copy';
const count = z.string().regex(/^\d+$/u);
const schema = z.object({
  settings: memorySettingsSchema,
  revision: z.object({
    knowledge_revision: z.string(),
    policy_revision: z.string(),
  }),
  entries: z.array(
    z.object({
      id: z.string(),
      plan_json: memoryPlanSchema,
      status: z.enum(['candidate', 'active', 'disabled', 'rejected']),
      validation_code: z.string(),
      engine_version: z.number(),
      knowledge_revision: z.string(),
      policy_revision: z.string(),
      created_at: z.string(),
      expires_at: z.string(),
      validated_at: z.string().nullable(),
      hit_count: count,
      disabled_count: count,
    })
  ),
  nextCursor: z.string().nullable(),
  metrics: z.array(
    z.object({
      route: z.enum(['memory', 'luna']),
      turns: count,
      model_calls: count,
      cost_micro_usd: count,
      average_latency_ms: z.number(),
    })
  ),
  learning: z.array(
    z.object({ status: z.string(), count, cost_micro_usd: count })
  ),
});
export function ClientMemory({
  scope,
  session,
  copy,
  language,
  canAdmin,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  canAdmin: boolean;
}): JSX.Element {
  const [cursor, setCursor] = useState<string | null>(null);
  const result = useRemote(
    botPath(scope, 'memory') +
      (cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''),
    schema
  );
  const mutation = useMutation();
  const money = (value: string) =>
    formatExactMoney(
      BigInt(value),
      'USD',
      6,
      language === 'es' ? 'es-PE' : 'en-US'
    );
  const totalTurns =
    result.data?.metrics.reduce((sum, row) => sum + BigInt(row.turns), 0n) ??
    null;
  const hits = result.data?.metrics.find((row) => row.route === 'memory');
  return (
    <section className="settings-content">
      <h2>{copy.memoryTitle}</h2>
      <p>{copy.memoryHelp}</p>
      <ErrorNotice
        error={result.error ?? mutation.error}
        copy={copy}
        onRetry={result.reload}
      />
      {result.loading && !result.data && <p>{copy.loading}</p>}
      {result.data && (
        <>
          {canAdmin && (
            <form
              key={JSON.stringify(result.data.settings)}
              onSubmit={(event) => {
                const data = new FormData(event.currentTarget);
                onSubmit(event, () =>
                  mutation.run(async () => {
                    await request(
                      botPath(scope, 'memory/settings'),
                      savedSchema,
                      {
                        csrf: session.csrf,
                        body: {
                          enabled: data.has('enabled'),
                          learningEnabled: data.has('learningEnabled'),
                          dailyLearningLimit: Number(
                            data.get('dailyLearningLimit')
                          ),
                        },
                      }
                    );
                    result.reload();
                  })
                );
              }}
            >
              <label>
                <input
                  name="enabled"
                  type="checkbox"
                  defaultChecked={result.data.settings.enabled}
                />
                {copy.memoryEnabled}
              </label>
              <label>
                <input
                  name="learningEnabled"
                  type="checkbox"
                  defaultChecked={result.data.settings.learningEnabled}
                />
                {copy.memoryLearningEnabled}
              </label>
              <Field label={copy.memoryDailyLimit}>
                <input
                  name="dailyLearningLimit"
                  type="number"
                  min="0"
                  max="100"
                  required
                  defaultValue={result.data.settings.dailyLearningLimit}
                />
              </Field>
              <button disabled={mutation.busy}>{copy.save}</button>
            </form>
          )}
          <h3>{copy.memoryMetrics}</h3>
          <p>{copy.memoryMetricsHelp}</p>
          {totalTurns !== null && totalTurns > 0n ? (
            <p>
              {copy.memoryCoverage}:{' '}
              {(
                (BigInt(hits?.turns ?? '0') * 10000n) /
                totalTurns /
                100n
              ).toString()}
              %
            </p>
          ) : (
            <p>{copy.memoryNoMeasurements}</p>
          )}
          <table>
            <thead>
              <tr>
                <th>{copy.memoryRoute}</th>
                <th>{copy.memoryTurns}</th>
                <th>{copy.memoryCalls}</th>
                <th>{copy.memoryCost}</th>
                <th>{copy.memoryLatency}</th>
              </tr>
            </thead>
            <tbody>
              {result.data.metrics.map((row) => (
                <tr key={row.route}>
                  <td>
                    {row.route === 'memory'
                      ? copy.memoryTitle
                      : copy.memoryLuna}
                  </td>
                  <td>{row.turns}</td>
                  <td>{row.model_calls}</td>
                  <td>{money(row.cost_micro_usd)}</td>
                  <td>{(row.average_latency_ms / 1000).toFixed(2)} s</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h3>{copy.memoryLearning}</h3>
          {result.data.learning.map((row) => (
            <p key={row.status}>
              {row.status}: {row.count} · {copy.memoryCost}:{' '}
              {money(row.cost_micro_usd)}
            </p>
          ))}
          <h3>{copy.memoryEntries}</h3>
          {!result.data.entries.length && <p>{copy.memoryEmpty}</p>}
          {result.data.entries.map((entry) => {
            const current =
              entry.engine_version === MEMORY_ENGINE_VERSION &&
              entry.knowledge_revision ===
                result.data!.revision.knowledge_revision &&
              entry.policy_revision === result.data!.revision.policy_revision &&
              Date.parse(entry.expires_at) > Date.now();
            return (
              <article key={entry.id} className="card">
                <h4>{entry.plan_json.title}</h4>
                <p>
                  {entry.status} ·{' '}
                  {current ? copy.memoryCurrent : copy.memoryStale} ·{' '}
                  {entry.plan_json.locale}
                </p>
                <p>
                  {copy.memoryHits}: {entry.hit_count} ·{' '}
                  {copy.memoryCorrections}: {entry.disabled_count}
                </p>
                <p>
                  {copy.memoryExpires}: {dateLabel(entry.expires_at, language)}
                </p>
                <details>
                  <summary>{copy.memoryInspect}</summary>
                  <ul>
                    {entry.plan_json.examples.map((example) => (
                      <li key={example}>{example}</li>
                    ))}
                  </ul>
                  {entry.plan_json.kind === 'knowledge' ? (
                    <p>{entry.plan_json.answer}</p>
                  ) : (
                    <pre>
                      {JSON.stringify(
                        {
                          binding: entry.plan_json.binding,
                          query: entry.plan_json.query,
                          criteria: entry.plan_json.propertySearch,
                        },
                        null,
                        2
                      )}
                    </pre>
                  )}
                  <p>{entry.validation_code}</p>
                  <p>
                    {copy.memoryRevision}: {entry.knowledge_revision} /{' '}
                    {entry.policy_revision}
                  </p>
                </details>
                {canAdmin &&
                  (entry.status === 'active' ||
                    (entry.status === 'disabled' &&
                      entry.validated_at &&
                      current)) && (
                    <button
                      disabled={mutation.busy}
                      onClick={() =>
                        void mutation.run(async () => {
                          await request(
                            botPath(scope, `memory/${entry.id}`),
                            savedSchema,
                            {
                              csrf: session.csrf,
                              body: {
                                status:
                                  entry.status === 'active'
                                    ? 'disabled'
                                    : 'active',
                              },
                            }
                          );
                          result.reload();
                        })
                      }
                    >
                      {entry.status === 'active'
                        ? copy.memoryDisable
                        : copy.memoryRestore}
                    </button>
                  )}
              </article>
            );
          })}
          <button
            disabled={!cursor || mutation.busy}
            onClick={() => setCursor(null)}
          >
            {copy.memoryFirstPage}
          </button>
          {result.data.nextCursor && (
            <button onClick={() => setCursor(result.data!.nextCursor)}>
              {copy.memoryNextPage}
            </button>
          )}
        </>
      )}
    </section>
  );
}
